import React, { useEffect, useState } from "react";
import { api, fmtDate, fmtTime } from "../api";
import { useApp } from "../context/AppContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Languages, ChevronDown, ChevronUp, Copy, Loader2, User, HardHat, Bot } from "lucide-react";
import { toast } from "sonner";

const SPEAKER = {
  customer: { icon: User, cls: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30" },
  installer: { icon: HardHat, cls: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  buddy: { icon: Bot, cls: "bg-primary/10 text-primary border-primary/30" },
};

const Turn = ({ tn, t, installerName }) => {
  const sp = SPEAKER[tn.speaker] || SPEAKER.buddy;
  const Icon = sp.icon;
  const who = tn.speaker === "customer" ? t("interp_customer") : tn.speaker === "installer" ? (installerName || t("installer")) : "Buddy";
  const label = tn.kind === "intro" ? t("interp_intro_label") : tn.kind === "exit" ? t("interp_exit_label") : who;
  return (
    <div className={`rounded-xl border px-3 py-2 ${sp.cls}`} data-testid={`talk-turn-${tn.speaker}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide opacity-80">
        <Icon className="h-3 w-3" /> {label} <span className="ml-auto font-normal normal-case">{fmtTime(tn.at)}</span>
      </div>
      <div className="text-sm mt-0.5 text-foreground">{tn.original}</div>
      {tn.translated && tn.translated !== tn.original && (
        <div className="text-xs mt-0.5 text-muted-foreground italic">→ {tn.translated}</div>
      )}
    </div>
  );
};

const Session = ({ s, t, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const copy = async () => {
    const txt = s.turns.map((x) => `${fmtTime(x.at)} ${x.speaker === "customer" ? t("interp_customer") : x.speaker === "installer" ? (s.installer_name || t("installer")) : "Buddy"}: ${x.original}${x.translated && x.translated !== x.original ? ` → ${x.translated}` : ""}`).join("\n");
    try { await navigator.clipboard.writeText(txt); toast.success(t("copied")); } catch (e) { toast.error(t("something_wrong")); }
  };
  return (
    <Card className="p-3" data-testid="talk-session">
      <button type="button" className="w-full flex items-center gap-2 text-left" onClick={() => setOpen((o) => !o)} data-testid="talk-session-toggle">
        <Languages className="h-4 w-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{fmtDate(s.started_at)} · {fmtTime(s.started_at)}{s.ended_at !== s.started_at ? ` – ${fmtTime(s.ended_at)}` : ""}</div>
          <div className="text-[11px] text-muted-foreground truncate">{s.installer_name} · {s.count} {t("interp_turns")} · {s.customer_said} {t("interp_from_customer")}</div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && (
        <div className="mt-3 space-y-1.5">
          {s.turns.map((tn) => <Turn key={tn.id} tn={tn} t={t} installerName={s.installer_name} />)}
          <div className="flex justify-end pt-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={copy} data-testid="talk-session-copy"><Copy className="h-3 w-3 mr-1" /> {t("copy")}</Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export const JobCustomerTalks = ({ jobId }) => {
  const { t } = useApp();
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/jobs/${jobId}/interpreter-sessions`).then(({ data }) => { if (alive) setItems(data || []); }).catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [jobId]);
  if (items === null) return <div className="flex justify-center py-10 text-muted-foreground" data-testid="talks-loading"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return (
    <div className="max-w-3xl mx-auto w-full p-4 space-y-3" data-testid="job-customer-talks">
      <p className="text-xs text-muted-foreground">{t("interp_talks_hint")}</p>
      {items.length === 0 ? (
        <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="talks-empty">{t("interp_talks_empty")}</Card>
      ) : items.map((s, i) => <Session key={s.id} s={s} t={t} defaultOpen={i === 0} />)}
    </div>
  );
};
